import React, { useCallback, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  EDITOR_UI,
  MOCKUP_EXPORT_ZOOM_SCALE,
} from "./mockupGeneratorEditorStyles";

/** Zoom follows pointer: transform-origin tracks cursor inside the clip area. */
export function MockupExportPreview({
  src,
  alt,
  fixedHeight,
}: {
  src: string;
  alt: string;
  fixedHeight?: number;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);

  const applyOrigin = useCallback((wrap: HTMLDivElement, clientX: number, clientY: number) => {
    const r = wrap.getBoundingClientRect();
    const w = r.width || 1;
    const h = r.height || 1;
    const x = Math.min(100, Math.max(0, ((clientX - r.left) / w) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - r.top) / h) * 100));
    const el = imgRef.current;
    if (el) el.style.transformOrigin = `${x}% ${y}%`;
  }, []);

  const handlePointerEnter = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => applyOrigin(e.currentTarget, e.clientX, e.clientY),
    [applyOrigin],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => applyOrigin(e.currentTarget, e.clientX, e.clientY),
    [applyOrigin],
  );

  const handlePointerLeave = useCallback(() => {
    const el = imgRef.current;
    if (el) el.style.transformOrigin = "50% 50%";
  }, []);

  return (
    <Box
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      sx={{
        overflow: "hidden",
        borderRadius: 1,
        mb: 1,
        cursor: "zoom-in",
        touchAction: "none",
        height: fixedHeight ?? "auto",
        "&:hover img": { transform: `scale(${MOCKUP_EXPORT_ZOOM_SCALE})` },
      }}
    >
      <Box
        ref={imgRef}
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: "100%",
          height: fixedHeight ?? "auto",
          display: "block",
          borderRadius: 1,
          transition: "transform 0.22s ease-out",
          transformOrigin: "50% 50%",
          objectFit: "contain",
        }}
      />
    </Box>
  );
}

export function EmptyStatePanel({
  message,
  title = "Nothing here yet",
  minHeight = 420,
}: {
  message: string;
  title?: string;
  minHeight?: number;
}) {
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2.5,
        minHeight,
        ...(minHeight > 0
          ? { transform: { xs: "translateY(-14px)", md: "translateY(-24px)" } }
          : {}),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        bgcolor: "transparent",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 3,
          p: 3,
          border: "1px solid",
          borderColor: "rgba(99, 102, 241, 0.18)",
          background:
            "linear-gradient(180deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.06) 100%)",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            mx: "auto",
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.8)",
            border: "1px solid rgba(99, 102, 241, 0.22)",
          }}
        >
          <ImageNotSupportedOutlinedIcon sx={{ fontSize: 28, color: "text.secondary" }} />
        </Box>
        <CustomTypography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
          {title}
        </CustomTypography>
        <CustomTypography variant="caption" color="text.secondary">
          {message}
        </CustomTypography>
      </Box>
    </Box>
  );
}

export function EditorPanelCard({
  icon,
  title,
  children,
  defaultExpanded = true,
}: {
  icon?: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 2,
        bgcolor: "transparent",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
        sx={{
          px: 0,
          minHeight: 44,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          {icon ? (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(79, 70, 229, 0.08)",
                color: EDITOR_UI.primary,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <CustomTypography variant="body2" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </CustomTypography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: "common.white",
            border: "1px solid",
            borderColor: EDITOR_UI.border,
            boxShadow: EDITOR_UI.cardShadow,
          }}
        >
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
